/* eslint-disable @typescript-eslint/no-unused-vars */
import { TelegramInitData, TelegramUser } from '@/types'

// Локальные настройки мока живут в .env.local (он в .gitignore) — сам файл
// обязан оставаться в git, иначе прод-сборка падает на TS2307: он статически
// импортируется из main.tsx и useTelegram.ts.
//   VITE_MOCK_TELEGRAM_ID   — telegram_id, под которым входим локально
//   VITE_MOCK_TELEGRAM_OFF  — 'true', чтобы показать веб-логин вместо мока
//   VITE_MOCK_EMAIL         — вход по email вместо телеграмного мока (см. devMockAuth.ts)
const mockTelegramId = Number(import.meta.env.VITE_MOCK_TELEGRAM_ID ?? 0) || 0

// Dev toggle: true — принудительно показать экран веб-логина вместо Mock Telegram.
const web = import.meta.env.VITE_MOCK_TELEGRAM_OFF === 'true'

// Мок-вход по email (педагогика и любые аккаунты без telegram_id) — приоритетнее
// телеграмного: см. src/lib/devMockAuth.ts и каталог входов в .env.local.
const mockEmail = String(import.meta.env.VITE_MOCK_EMAIL ?? '').trim()

// Мок-пользователь для Telegram Web App
// ВАЖНО: НЕ указывать реальные first_name/last_name/tg_username разработчика!
// При mock-входе эти поля могли перезатирать данные целевого пользователя в БД.
// Оставляем только telegram_id — реальные данные будут прочитаны из БД в AuthProvider.
const mockTelegramUser: TelegramUser = {
  id: mockTelegramId,
  first_name: '',
  tg_username: '',
  language_code: 'ru',
  is_premium: false
}

type MockTelegramInitData = TelegramInitData & { raw: string }

export const getMockTelegramData = (): MockTelegramInitData => {
  const authDate = Math.floor(Date.now() / 1000);
  const mockData: MockTelegramInitData = {
    user: mockTelegramUser,
    chat_instance: 'mock-chat-instance',
    auth_date: authDate,
    hash: 'mock-hash',
    raw: ''
  };

  // Создаём правильную raw строку для Telegram initData
  const userJson = JSON.stringify({
    id: mockTelegramUser.id,
    first_name: mockTelegramUser.first_name,
    last_name: mockTelegramUser.last_name,
    username: mockTelegramUser.tg_username,
    language_code: mockTelegramUser.language_code,
    is_premium: mockTelegramUser.is_premium
  });

  mockData.raw = `user=${encodeURIComponent(userJson)}&auth_date=${authDate}&chat_instance=mock-chat-instance&hash=mock-hash`;

  return mockData;
}

// ⚠️ initData генерируется ЛЕНИВО, внутри initMockTelegramWebApp().
// Раньше здесь стоял вызов на уровне модуля — и из-за него Rollup не мог выкинуть
// этот файл из прод-сборки: вызов на верхнем уровне не считается чистым, поэтому
// мок ехал на прод мёртвым грузом (замер 22.08.2026). Экспортируемые функции сами
// по себе выкидываются нормально.
function buildMockWebApp() {
  const mockInitDataObj = getMockTelegramData();
  const mockInitDataString = mockInitDataObj.raw;

  return {
  initData: mockInitDataString, // Теперь это правильная строка с параметрами
  initDataUnsafe: {
    user: {
      id: mockTelegramUser.id,
      first_name: mockTelegramUser.first_name,
      last_name: mockTelegramUser.last_name,
      username: mockTelegramUser.tg_username, // Telegram использует 'username', не 'tg_username'
      language_code: mockTelegramUser.language_code,
      is_premium: mockTelegramUser.is_premium
    },
    auth_date: mockInitDataObj.auth_date,
    chat_instance: mockInitDataObj.chat_instance,
    hash: mockInitDataObj.hash
  },
  version: '7.0',
  platform: 'web',
  /**
   * ⚠️ Геттер, а не константа `'light'`.
   *
   * `readSystemScheme()` в src/lib/theme.ts доверяет
   * `Telegram.WebApp.colorScheme` больше, чем `prefers-color-scheme` (внутри
   * Telegram схему задаёт клиент, а не ОС). Мок существует и в локальной
   * разработке, поэтому с зашитым `'light'` режим темы «как в системе»
   * никогда не переключался бы на тёмную на dev-машине — и выглядел бы
   * сломанным, хотя в бою работает.
   */
  get colorScheme(): 'light' | 'dark' {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  },
  themeParams: {
    bg_color: '#ffffff',
    text_color: '#000000',
    hint_color: '#999999',
    link_color: '#007aff',
    button_color: '#007aff',
    button_text_color: '#ffffff',
    secondary_bg_color: '#f5f5f5'
  },
  isExpanded: true,
  viewportHeight: 600,
  viewportStableHeight: 600,
  headerColor: '#ffffff',
  backgroundColor: '#ffffff',
  isClosingConfirmationEnabled: false,

  // Упрощенные методы без сложной логики
  MainButton: {
    text: '',
    color: '#007aff',
    textColor: '#ffffff',
    isVisible: false,
    isProgressVisible: false,
    isActive: true,
    setText: (text: string) => { console.log('MainButton.setText:', text) },
    onClick: (callback: () => void) => { console.log('MainButton.onClick registered') },
    offClick: (callback: () => void) => { console.log('MainButton.offClick registered') },
    show: () => { console.log('MainButton.show') },
    hide: () => { console.log('MainButton.hide') },
    enable: () => { console.log('MainButton.enable') },
    disable: () => { console.log('MainButton.disable') },
    showProgress: () => { console.log('MainButton.showProgress') },
    hideProgress: () => { console.log('MainButton.hideProgress') }
  },

  BackButton: {
    isVisible: false,
    onClick: (callback: () => void) => { console.log('BackButton.onClick registered') },
    offClick: (callback: () => void) => { console.log('BackButton.offClick registered') },
    show: () => { console.log('BackButton.show') },
    hide: () => { console.log('BackButton.hide') }
  },

  HapticFeedback: {
    impactOccurred: (style: string) => { console.log('HapticFeedback.impactOccurred:', style) },
    notificationOccurred: (type: string) => { console.log('HapticFeedback.notificationOccurred:', type) },
    selectionChanged: () => { console.log('HapticFeedback.selectionChanged') }
  },

  expand: () => { console.log('WebApp.expand') },
  close: () => { console.log('WebApp.close') },
  ready: () => { console.log('WebApp.ready') },
  requestContact: (_callback: (contact: { phone_number: string }) => void) => { console.log('WebApp.requestContact') },
  showAlert: (message: string, callback?: () => void) => {
    console.log('WebApp.showAlert:', message)
    alert(message)
    callback?.()
  },
  showConfirm: (message: string, callback: (confirmed: boolean) => void) => {
    console.log('WebApp.showConfirm:', message)
    callback(confirm(message))
  },
  showPopup: (params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: string; text: string }> }, callback?: (buttonId: string) => void) => {
    console.log('WebApp.showPopup:', params)
    alert(params.message)
    callback?.('ok')
  },
  setHeaderColor: (color: string) => { console.log('WebApp.setHeaderColor:', color) },
  setBackgroundColor: (color: string) => { console.log('WebApp.setBackgroundColor:', color) },
  enableClosingConfirmation: () => { console.log('WebApp.enableClosingConfirmation') },
  disableClosingConfirmation: () => { console.log('WebApp.disableClosingConfirmation') }
  };
}

/**
 * Инициализирует мок Telegram WebApp для локальной разработки
 * Вызывается автоматически в режиме разработки, если Telegram WebApp недоступен
 */
export function initMockTelegramWebApp() {
  if (typeof window === 'undefined') return

  if (web) {
    console.log('ℹ️ Mock Telegram disabled (VITE_MOCK_TELEGRAM_OFF=true)')
    return
  }

  // Выбран вход по email — телеграмный мок не ставим вовсе. Иначе AuthProvider
  // видел бы «мы в Telegram» и часть экранов вела бы себя как в Telegram,
  // хотя человек вошёл под аккаунтом, у которого telegram_id нет вовсе.
  if (mockEmail) {
    console.log('ℹ️ Mock Telegram skipped: VITE_MOCK_EMAIL =', mockEmail)
    return
  }

  if (!mockTelegramId) {
    console.warn(
      '⚠️ VITE_MOCK_TELEGRAM_ID не задан — мок-вход не сработает. ' +
      'Добавьте VITE_MOCK_TELEGRAM_ID=<ваш telegram_id> в .env.local и перезапустите dev-сервер'
    )
  }

  // Проверяем, не существует ли уже реальный Telegram WebApp
  if (window.Telegram?.WebApp?.initData) {
    console.log('✅ Real Telegram WebApp detected, skipping mock initialization')
    return
  }

  console.log('🔧 Initializing mock Telegram WebApp for local development')

  const mockWebApp = buildMockWebApp()

  // Создаем структуру window.Telegram если её нет
  // Устанавливаем мок WebApp. В dev-режиме допустимо создать Telegram namespace,
  // если Telegram SDK ещё не инициализировал его сам.
  if (!window.Telegram) {
    Object.defineProperty(window, 'Telegram', {
      configurable: true,
      writable: true,
      value: { WebApp: mockWebApp }
    })
  } else {
    window.Telegram.WebApp = mockWebApp
  }

  console.log('✅ Mock Telegram WebApp initialized successfully')
  console.log('👤 Mock user:', mockTelegramUser)
}
